import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush
} from 'recharts';
import { Icons } from '../components/Icons';
import {
  BitcoinCyclesDataService,
  BitcoinDataPoint,
  PHASE_COLORS
} from '../services/bitcoinCyclesData';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import {
  BITCOIN_HISTORICAL_EVENTS,
  BitcoinEvent,
  getEventColor,
  EVENT_STATS
} from '../data/bitcoinHistoricalEvents';
import './bitcoinCycles.css';

// Type for active tab
type ActiveTab = 'cycles' | 'fud-timeline';

// Dates des halvings Bitcoin (de CYCLE_METADATA)
const HALVING_EVENTS = [
  { date: new Date('2012-11-28'), timestamp: new Date('2012-11-28').getTime(), label: 'Halving 1', cycle: 1 },
  { date: new Date('2016-07-09'), timestamp: new Date('2016-07-09').getTime(), label: 'Halving 2', cycle: 2 },
  { date: new Date('2020-05-11'), timestamp: new Date('2020-05-11').getTime(), label: 'Halving 3', cycle: 3 },
  { date: new Date('2024-04-20'), timestamp: new Date('2024-04-20').getTime(), label: 'Halving 4', cycle: 4 },
];

// Type pour les données du graphique (avec aires séparées par phase)
interface ChartDataPoint extends BitcoinDataPoint {
  bearMarketValue: number;
  accumulationValue: number;
  markupValue: number;
  distributionValue: number;
}

export default function BitcoinCycles() {
  const { t } = useTranslation();
  const { language } = useLanguage(); // Force re-render on language change
  const lang = language === 'en' ? 'en' : 'fr';

  const [data, setData] = useState<BitcoinDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'all' | '1y' | '4y'>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('cycles');
  const [isMobile, setIsMobile] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<BitcoinEvent | null>(null);

  // Read tab from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'fud-timeline' || hash === 'btc-vs-fud') {
      setActiveTab('fud-timeline');
    } else if (hash === 'cycles') {
      setActiveTab('cycles');
    }

    // Listen for hash changes (browser back/forward)
    const handleHashChange = () => {
      const newHash = window.location.hash.replace('#', '');
      if (newHash === 'fud-timeline' || newHash === 'btc-vs-fud') {
        setActiveTab('fud-timeline');
      } else {
        setActiveTab('cycles');
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Update URL hash when tab changes
  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    window.history.pushState(null, '', `#${tab}`);
  };

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Récupérer le prix actuel du Bitcoin via CoinGecko Pro (avec cache)
  const { data: btcPriceData } = useCoinPrices(['bitcoin'], {
    refetchInterval: 60000 // Refresh every 60 seconds
  });
  const currentBtcPrice = btcPriceData?.bitcoin?.usd || null;
  const btcPriceChange24h = btcPriceData?.bitcoin?.usd_24h_change || null;

  // État des calques (layers) visibles
  const [layers, setLayers] = useState({
    rainbow: true,
    miningCosts: true,
    halvings: true,
    phases: true,
  });

  // Charger les données au montage
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const cyclesData = await BitcoinCyclesDataService.getBitcoinCyclesData(
        '2013-01-01',
        '2026-12-31',
        undefined, // Use default mining config
        forceRefresh
      );

      setData(cyclesData);
    } catch (err) {
      console.error('Error loading Bitcoin cycles data:', err);
      setError(t('bitcoinCycles.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData(true); // Force refresh
    setIsRefreshing(false);
  };

  // Transformer les données pour le graphique avec aires séparées et Rainbow bands
  const chartData: ChartDataPoint[] = useMemo(() => {
    return data.map(point => ({
      ...point,
      bearMarketValue: point.phase === 'Bear Market' ? point.fractalValue : 0,
      accumulationValue: point.phase === 'Accumulation' ? point.fractalValue : 0,
      markupValue: point.phase === 'Markup' ? point.fractalValue : 0,
      distributionValue: point.phase === 'Distribution' ? point.fractalValue : 0,
      // Rainbow bands (extraire pour accès facile)
      rainbow1: point.rainbowBands?.band1,
      rainbow3: point.rainbowBands?.band3,
      rainbow5: point.rainbowBands?.band5,
      rainbow7: point.rainbowBands?.band7,
      rainbow9: point.rainbowBands?.band9,
    }));
  }, [data]);

  // Filtrer les données selon la période sélectionnée
  const filteredData = useMemo(() => {
    if (selectedPeriod === 'all') return chartData;

    const now = Date.now();
    const cutoff = selectedPeriod === '1y'
      ? now - 365 * 24 * 60 * 60 * 1000
      : now - 4 * 365 * 24 * 60 * 60 * 1000;

    return chartData.filter(d => d.timestamp >= cutoff);
  }, [chartData, selectedPeriod]);

  // Prepare FUD timeline data - merge price data with events
  const fudTimelineData = useMemo(() => {
    // Use all data for the FUD timeline (from earliest date)
    const priceData = chartData.map(point => ({
      timestamp: point.timestamp,
      price: point.price,
      date: point.date,
    }));

    return priceData;
  }, [chartData]);

  // Major events to show on mobile (most impactful moments in BTC history)
  const MAJOR_EVENT_IDS = [
    'first-btc-transaction', // Pizza day
    'mtgox-collapse', // Mt. Gox
    'halving-2', // Halving
    'btc-ath-2017', // ATH
    'china-ban-3', // Major China ban
    'halving-3', // Halving
    'el-salvador-legal-tender', // Major adoption
    'btc-ath-2021', // ATH
    'ftx-collapse', // Major crash
    'spot-etf-approved', // Historic approval
    'halving-4', // Halving
    'trump-strategic-reserve', // Major policy
    'btc-ath-2024-dec', // Current ATH
  ];

  // Filter events based on selected period and mobile
  const filteredEvents = useMemo(() => {
    let events = BITCOIN_HISTORICAL_EVENTS;

    // Filter by period
    if (selectedPeriod !== 'all') {
      const now = Date.now();
      const cutoff = selectedPeriod === '1y'
        ? now - 365 * 24 * 60 * 60 * 1000
        : now - 4 * 365 * 24 * 60 * 60 * 1000;
      events = events.filter(e => e.timestamp >= cutoff);
    }

    // On mobile, show only major events to reduce clutter
    if (isMobile) {
      events = events.filter(e => MAJOR_EVENT_IDS.includes(e.id));
    }

    return events;
  }, [selectedPeriod, isMobile]);

  // Find nearby event for a given timestamp (within 30 days)
  const findNearbyEvent = (timestamp: number) => {
    const threshold = 30 * 24 * 60 * 60 * 1000; // 30 days
    return filteredEvents.find(e => Math.abs(e.timestamp - timestamp) < threshold);
  };

  // Handle chart click to show event details
  const handleChartClick = (data: any) => {
    if (data?.activePayload?.[0]?.payload) {
      const point = data.activePayload[0].payload;
      const nearbyEvent = findNearbyEvent(point.timestamp);
      if (nearbyEvent) {
        setSelectedEvent(nearbyEvent);
      }
    }
  };

  // Event Detail Modal Component
  const EventDetailModal = () => {
    if (!selectedEvent) return null;

    return (
      <div
        className="event-detail-modal-overlay"
        onClick={() => setSelectedEvent(null)}
      >
        <div
          className="event-detail-modal"
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'relative' }}
        >
          {/* Close button */}
          <button
            onClick={() => setSelectedEvent(null)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}
          >
            <Icons.X size={18} />
          </button>

          {/* Header with icon and type badge */}
          <div style={{
            padding: '24px',
            borderBottom: '1px solid var(--border)',
            background: `linear-gradient(135deg, ${getEventColor(selectedEvent.type)}10, transparent)`
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <span style={{ fontSize: '48px' }}>{selectedEvent.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{
                    fontSize: '12px',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    background: `${getEventColor(selectedEvent.type)}20`,
                    color: getEventColor(selectedEvent.type),
                    fontWeight: 700,
                    textTransform: 'uppercase'
                  }}>
                    {selectedEvent.type === 'fud' ? 'FUD' : selectedEvent.type === 'positive' ? (lang === 'fr' ? 'Positif' : 'Positive') : 'Neutral'}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--muted)' }}>
                    {new Date(selectedEvent.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <h2 style={{ margin: 0, fontSize: '20px', color: 'var(--text)', lineHeight: 1.3 }}>
                  {selectedEvent.title[lang]}
                </h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '24px' }}>
            <p style={{
              fontSize: '15px',
              color: 'var(--text)',
              lineHeight: '1.7',
              margin: '0 0 20px 0'
            }}>
              {selectedEvent.description[lang]}
            </p>

            {/* Price info */}
            {selectedEvent.priceAtEvent && (
              <div style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--muted)', fontSize: '14px' }}>
                    {lang === 'fr' ? 'Prix du Bitcoin à cette date' : 'Bitcoin price at this date'}
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: 700, color: '#f7931a' }}>
                    ${selectedEvent.priceAtEvent.toLocaleString()}
                  </span>
                </div>
                {currentBtcPrice && (
                  <div style={{
                    marginTop: '12px',
                    paddingTop: '12px',
                    borderTop: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: 'var(--muted)', fontSize: '13px' }}>
                      {lang === 'fr' ? 'Évolution depuis' : 'Change since then'}
                    </span>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: currentBtcPrice > selectedEvent.priceAtEvent ? '#10b981' : '#ef4444'
                    }}>
                      {currentBtcPrice > selectedEvent.priceAtEvent ? '+' : ''}
                      {(((currentBtcPrice - selectedEvent.priceAtEvent) / selectedEvent.priceAtEvent) * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Source */}
            {selectedEvent.source && (
              <div style={{
                fontSize: '13px',
                color: 'var(--muted)',
                fontStyle: 'italic'
              }}>
                Source: {selectedEvent.source}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={() => setSelectedEvent(null)}
              style={{
                padding: '10px 20px',
                background: 'var(--accent)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {lang === 'fr' ? 'Fermer' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Custom tooltip for FUD Timeline
  const FudTimelineTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const point = payload[0]?.payload;
    if (!point) return null;

    // Find nearby event
    const nearbyEvent = findNearbyEvent(point.timestamp);

    return (
      <div className="bitcoin-cycles-tooltip" style={{ minWidth: '280px', maxWidth: '380px' }}>
        {/* Date and Price */}
        <p className="tooltip-date" style={{ marginBottom: nearbyEvent ? '12px' : '8px' }}>
          {new Date(point.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
        <div className="tooltip-row" style={{ marginBottom: nearbyEvent ? '12px' : '0' }}>
          <span className="tooltip-label">💰 {lang === 'fr' ? 'Prix BTC:' : 'BTC Price:'}</span>
          <span className="tooltip-value price" style={{ fontWeight: 700 }}>
            ${point.price?.toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-US', { maximumFractionDigits: 0 })}
          </span>
        </div>

        {/* Nearby Event Info */}
        {nearbyEvent && (
          <div style={{
            borderTop: '1px solid var(--border)',
            paddingTop: '12px',
            marginTop: '4px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '20px' }}>{nearbyEvent.icon}</span>
              <span style={{
                color: getEventColor(nearbyEvent.type),
                fontWeight: 700,
                fontSize: '11px',
                textTransform: 'uppercase',
                padding: '2px 6px',
                background: `${getEventColor(nearbyEvent.type)}20`,
                borderRadius: '4px'
              }}>
                {nearbyEvent.type === 'fud' ? 'FUD' : nearbyEvent.type === 'positive' ? (lang === 'fr' ? 'POSITIF' : 'POSITIVE') : 'NEUTRAL'}
              </span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong style={{ color: 'var(--text)', fontSize: '14px' }}>{nearbyEvent.title[lang]}</strong>
            </div>
            <p style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.4', margin: 0 }}>
              {nearbyEvent.description[lang].length > 120
                ? nearbyEvent.description[lang].substring(0, 120) + '...'
                : nearbyEvent.description[lang]}
            </p>
            {nearbyEvent.priceAtEvent && (
              <div style={{ marginTop: '8px', fontSize: '11px' }}>
                <span style={{ color: 'var(--muted)' }}>
                  {lang === 'fr' ? 'Prix à l\'époque:' : 'Price at event:'}
                </span>
                <span style={{ fontWeight: 600, color: '#f7931a', marginLeft: '6px' }}>
                  ${nearbyEvent.priceAtEvent.toLocaleString()}
                </span>
              </div>
            )}
            <div style={{
              marginTop: '8px',
              fontSize: '10px',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Icons.ExternalLink size={10} />
              {lang === 'fr' ? 'Cliquer pour plus de détails' : 'Click for more details'}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Tooltip personnalisé enrichi
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload as BitcoinDataPoint;
    if (!data) return null;

    const formatPrice = (val: number | undefined) => {
      if (val === undefined || val === null || isNaN(val)) return 'N/A';
      if (val >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
      if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
      return `$${val.toFixed(0)}`;
    };

    // Définir les bandes Rainbow avec leurs couleurs
    const rainbowBands = [
      { name: 'Max Bubble', color: '#991b1b', value: data.rainbowBands?.band9, isActive: data.rainbowBands && data.price > data.rainbowBands.band9 },
      { name: 'Bubble', color: '#dc2626', value: data.rainbowBands?.band9, isActive: data.rainbowBands && data.price <= data.rainbowBands.band9 && data.price > data.rainbowBands.band7 },
      { name: 'FOMO', color: '#f97316', value: data.rainbowBands?.band7, isActive: data.rainbowBands && data.price <= data.rainbowBands.band7 && data.price > data.rainbowBands.band5 },
      { name: 'Hold', color: '#fbbf24', value: data.rainbowBands?.band5, isActive: data.rainbowBands && data.price <= data.rainbowBands.band5 && data.price > data.rainbowBands.band3 },
      { name: 'Accumulate', color: '#10b981', value: data.rainbowBands?.band3, isActive: data.rainbowBands && data.price <= data.rainbowBands.band3 && data.price > data.rainbowBands.band1 },
      { name: 'Fire Sale', color: '#4169E1', value: data.rainbowBands?.band1, isActive: data.rainbowBands && data.price <= data.rainbowBands.band1 },
    ];

    const activeZone = rainbowBands.find(b => b.isActive);

    return (
      <div className="bitcoin-cycles-tooltip" style={{ minWidth: '260px', maxWidth: '320px' }}>
        {/* Date */}
        <p className="tooltip-date">
          {new Date(data.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>

        <div className="tooltip-content">
        {/* Prix BTC */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          background: activeZone ? `${activeZone.color}20` : 'var(--hover)',
          borderRadius: '8px',
          marginBottom: '12px',
          border: activeZone ? `2px solid ${activeZone.color}` : '1px solid var(--border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              background: '#f7931a',
              boxShadow: '0 0 6px #f7931a'
            }} />
            <span style={{ fontWeight: 600, color: 'var(--text)', fontSize: '14px' }}>
              BTC
            </span>
          </div>
          <span style={{ fontWeight: 700, fontSize: '18px', color: 'var(--text)' }}>
            {formatPrice(data.price)}
          </span>
        </div>

        {/* Zone actuelle */}
        {activeZone && (
          <div style={{
            textAlign: 'center',
            marginBottom: '12px',
            padding: '6px 10px',
            background: `${activeZone.color}25`,
            borderRadius: '6px',
            color: activeZone.color,
            fontWeight: 700,
            fontSize: '13px'
          }}>
            Zone: {activeZone.name}
          </div>
        )}

        {/* Rainbow Bands - toujours affichées si données disponibles */}
        {data.rainbowBands && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
              {lang === 'fr' ? 'Bandes Rainbow' : 'Rainbow Bands'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {rainbowBands.map((band, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: band.isActive ? `${band.color}20` : 'transparent',
                  border: band.isActive ? `1px solid ${band.color}50` : 'none'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: band.color,
                      boxShadow: band.isActive ? `0 0 8px ${band.color}` : 'none',
                      border: band.isActive ? '2px solid white' : 'none'
                    }} />
                    <span style={{ fontSize: '12px', color: band.color, fontWeight: band.isActive ? 700 : 500 }}>
                      {band.name}
                    </span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: band.color }}>
                    {formatPrice(band.value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mining Costs */}
        {(data.miningCost || data.miningCostMin || data.miningCostMax) && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginBottom: '8px', fontWeight: 600, textTransform: 'uppercase' }}>
              {lang === 'fr' ? 'Coûts de Minage' : 'Mining Costs'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {data.miningCostMax && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '4px', background: '#ef4444', borderRadius: '2px' }} />
                    <span style={{ fontSize: '12px', color: '#ef4444' }}>Max</span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#ef4444' }}>{formatPrice(data.miningCostMax)}</span>
                </div>
              )}
              {data.miningCost && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '4px', background: '#f97316', borderRadius: '2px' }} />
                    <span style={{ fontSize: '12px', color: '#f97316' }}>{lang === 'fr' ? 'Moyen' : 'Avg'}</span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#f97316' }}>{formatPrice(data.miningCost)}</span>
                </div>
              )}
              {data.miningCostMin && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '4px', background: '#10b981', borderRadius: '2px' }} />
                    <span style={{ fontSize: '12px', color: '#10b981' }}>Min</span>
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#10b981' }}>{formatPrice(data.miningCostMin)}</span>
                </div>
              )}
            </div>
          </div>
        )}

          {/* Stats compactes */}
          <div style={{
            borderTop: '1px solid var(--border)',
            paddingTop: '8px',
            marginTop: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '10px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: 'var(--muted)' }}>Ratio</div>
              <div style={{ fontWeight: 600, color: data.price > data.miningCost ? '#10b981' : '#ef4444' }}>
                {(data.price / data.miningCost).toFixed(2)}x
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: 'var(--muted)' }}>Phase</div>
              <div style={{ fontWeight: 600, color: PHASE_COLORS[data.phase], fontSize: '9px' }}>
                {data.phase}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: 'var(--muted)' }}>Cycle</div>
              <div style={{ fontWeight: 600 }}>#{data.cycle}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Export data as JSON or CSV
  const handleExportData = (format: 'json' | 'csv') => {
    const exportData = data.map(point => ({
      date: point.date,
      price: point.price,
      phase: point.phase,
      cycle: point.cycle,
      miningCostMin: point.miningCostMin,
      miningCost: point.miningCost,
      miningCostMax: point.miningCostMax,
      rainbowBand1: point.rainbowBands?.band1 || null,
      rainbowBand3: point.rainbowBands?.band3 || null,
      rainbowBand5: point.rainbowBands?.band5 || null,
      rainbowBand7: point.rainbowBands?.band7 || null,
      rainbowBand9: point.rainbowBands?.band9 || null,
      fractalValue: point.fractalValue
    }));

    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(exportData, null, 2);
      filename = `bitcoin-cycles-data-${new Date().toISOString().split('T')[0]}.json`;
      mimeType = 'application/json';
    } else {
      // CSV format
      const headers = Object.keys(exportData[0]).join(',');
      const rows = exportData.map(row => Object.values(row).join(','));
      content = [headers, ...rows].join('\n');
      filename = `bitcoin-cycles-data-${new Date().toISOString().split('T')[0]}.csv`;
      mimeType = 'text/csv';
    }

    // Create download link
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Format pour l'axe des prix (logarithmique)
  const formatPriceLog = (value: number): string => {
    if (value >= 100000) return `${(value / 1000).toFixed(0)}k`;
    if (value >= 10000) return `${(value / 1000).toFixed(1)}k`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
    return value.toFixed(0);
  };

  // Format pour les dates
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="bitcoin-cycles-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('bitcoinCycles.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bitcoin-cycles-container">
        <div className="error-state">
          <Icons.AlertCircle size={48} />
          <h3>{t('bitcoinCycles.error')}</h3>
          <p>{error}</p>
          <button onClick={() => loadData()} className="retry-button">
            <Icons.RefreshCw size={16} />
            {t('bitcoinCycles.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bitcoin-cycles-container">
      {/* Header */}
      <div className="bitcoin-cycles-header">
        <div className="header-title">
          <h1>
            <Icons.TrendingUp size={32} style={{ color: 'var(--accent)' }} />
            {t('bitcoinCycles.title')}
          </h1>
          <p className="header-subtitle">
            {t('bitcoinCycles.subtitle')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Refresh Button */}
          <button
            className="retry-button"
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isRefreshing || loading ? 0.6 : 1,
              cursor: isRefreshing || loading ? 'not-allowed' : 'pointer'
            }}
          >
            <Icons.RefreshCw size={16} style={{
              animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
            }} />
            {isRefreshing ? t('bitcoinCycles.refreshing') : t('bitcoinCycles.refresh')}
          </button>

          {/* Export/Backup Buttons */}
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => handleExportData('json')}
              disabled={loading || data.length === 0}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text)',
                cursor: data.length === 0 ? 'not-allowed' : 'pointer',
                opacity: data.length === 0 ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
              title={lang === 'fr' ? 'Exporter en JSON' : 'Export as JSON'}
            >
              <Icons.Download size={14} />
              JSON
            </button>
            <button
              onClick={() => handleExportData('csv')}
              disabled={loading || data.length === 0}
              style={{
                padding: '8px 12px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                color: 'var(--text)',
                cursor: data.length === 0 ? 'not-allowed' : 'pointer',
                opacity: data.length === 0 ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
              title={lang === 'fr' ? 'Exporter en CSV' : 'Export as CSV'}
            >
              <Icons.Download size={14} />
              CSV
            </button>
          </div>

          {/* Period Selector */}
          <div className="period-selector">
            <button
              className={selectedPeriod === '1y' ? 'active' : ''}
              onClick={() => setSelectedPeriod('1y')}
            >
              {t('bitcoinCycles.period.1y')}
            </button>
            <button
              className={selectedPeriod === '4y' ? 'active' : ''}
              onClick={() => setSelectedPeriod('4y')}
            >
              {t('bitcoinCycles.period.4y')}
            </button>
            <button
              className={selectedPeriod === 'all' ? 'active' : ''}
              onClick={() => setSelectedPeriod('all')}
            >
              {t('bitcoinCycles.period.all')}
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation" style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        background: 'var(--card-bg)',
        padding: '4px',
        borderRadius: '12px',
        border: '1px solid var(--border)',
        width: isMobile ? '100%' : 'fit-content'
      }}>
        <button
          onClick={() => handleTabChange('cycles')}
          style={{
            padding: isMobile ? '10px 12px' : '12px 24px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'cycles' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'cycles' ? 'white' : 'var(--text)',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '4px' : '8px',
            transition: 'all 0.2s ease',
            flex: isMobile ? 1 : 'unset',
            fontSize: isMobile ? '12px' : '14px'
          }}
        >
          <Icons.TrendingUp size={isMobile ? 14 : 18} />
          {lang === 'fr' ? 'Cycles' : 'Cycles'}
        </button>
        <button
          onClick={() => handleTabChange('fud-timeline')}
          style={{
            padding: isMobile ? '10px 12px' : '12px 24px',
            borderRadius: '8px',
            border: 'none',
            background: activeTab === 'fud-timeline' ? 'var(--accent)' : 'transparent',
            color: activeTab === 'fud-timeline' ? 'white' : 'var(--text)',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: isMobile ? '4px' : '8px',
            transition: 'all 0.2s ease',
            flex: isMobile ? 1 : 'unset',
            fontSize: isMobile ? '12px' : '14px'
          }}
        >
          <Icons.AlertTriangle size={isMobile ? 14 : 18} />
          {lang === 'fr' ? 'BTC vs FUD' : 'BTC vs FUD'}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'cycles' && (
        <>
      {/* Layer Toggle Controls */}
      <div className="layer-controls" style={{
        background: 'var(--card-bg)',
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '24px',
        border: '1px solid var(--border)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'center'
      }}>
        <div style={{ fontWeight: 600, color: 'var(--text)', marginRight: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.Layers size={18} style={{ color: 'var(--accent)' }} />
          {t('bitcoinCycles.layers.title')}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={layers.phases}
            onChange={(e) => setLayers({ ...layers, phases: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px' }}>{t('bitcoinCycles.layers.phases')}</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={layers.rainbow}
            onChange={(e) => setLayers({ ...layers, rainbow: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px' }}>{t('bitcoinCycles.layers.rainbow')}</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={layers.miningCosts}
            onChange={(e) => setLayers({ ...layers, miningCosts: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px' }}>{t('bitcoinCycles.layers.miningCosts')}</span>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={layers.halvings}
            onChange={(e) => setLayers({ ...layers, halvings: e.target.checked })}
            style={{ cursor: 'pointer' }}
          />
          <span style={{ fontSize: '14px' }}>{t('bitcoinCycles.layers.halvings')}</span>
        </label>
      </div>

      {/* Info Cards */}
      <div className="info-cards">
        <div className="info-card">
          <div className="info-card-header">
            <Icons.Activity size={20} />
            <span>{t('bitcoinCycles.cards.currentCycle')}</span>
          </div>
          <div className="info-card-value">#{data[data.length - 1]?.cycle || 4}</div>
          <div className="info-card-label">{t('bitcoinCycles.cards.cycleLabel')}</div>
        </div>

        <div className="info-card">
          <div className="info-card-header">
            <Icons.TrendingUp size={20} />
            <span>{t('bitcoinCycles.cards.currentPhase')}</span>
          </div>
          <div
            className="info-card-value"
            style={{ color: PHASE_COLORS[data[data.length - 1]?.phase || 'Accumulation'] }}
          >
            {data[data.length - 1]?.phase || 'Accumulation'}
          </div>
          <div className="info-card-label">{t('bitcoinCycles.cards.marketState')}</div>
        </div>

        <div className="info-card">
          <div className="info-card-header">
            <Icons.DollarSign size={20} />
            <span>{t('bitcoinCycles.cards.currentPrice')}</span>
          </div>
          <div className="info-card-value">
            ${(currentBtcPrice || data[data.length - 1]?.price || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
          </div>
          <div className="info-card-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {t('bitcoinCycles.cards.btcPrice')}
            {btcPriceChange24h !== null && (
              <span style={{
                color: btcPriceChange24h >= 0 ? '#10b981' : '#ef4444',
                fontSize: '11px',
                fontWeight: 600
              }}>
                {btcPriceChange24h >= 0 ? '+' : ''}{btcPriceChange24h.toFixed(2)}%
              </span>
            )}
          </div>
        </div>

        <div className="info-card">
          <div className="info-card-header">
            <Icons.Zap size={20} />
            <span>{t('bitcoinCycles.cards.miningCost')}</span>
          </div>
          <div className="info-card-value">
            ${data[data.length - 1]?.miningCost.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) || '0'}
          </div>
          <div className="info-card-label">{t('bitcoinCycles.cards.avgUs')}</div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={600}>
          <ComposedChart
            data={filteredData}
            margin={{ top: 20, right: 80, left: 20, bottom: 20 }}
          >
            <defs>
              {/* Gradients pour les aires */}
              <linearGradient id="bearMarketGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PHASE_COLORS['Bear Market']} stopOpacity={0.6} />
                <stop offset="95%" stopColor={PHASE_COLORS['Bear Market']} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="accumulationGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PHASE_COLORS['Accumulation']} stopOpacity={0.6} />
                <stop offset="95%" stopColor={PHASE_COLORS['Accumulation']} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="markupGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PHASE_COLORS['Markup']} stopOpacity={0.7} />
                <stop offset="95%" stopColor={PHASE_COLORS['Markup']} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="distributionGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PHASE_COLORS['Distribution']} stopOpacity={0.6} />
                <stop offset="95%" stopColor={PHASE_COLORS['Distribution']} stopOpacity={0.1} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />

            <XAxis
              dataKey="timestamp"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatDate}
              stroke="var(--muted)"
              style={{ fontSize: '12px' }}
            />

            {/* Axe Y gauche: Valeurs fractales */}
            <YAxis
              yAxisId="fractal"
              orientation="left"
              stroke="var(--muted)"
              style={{ fontSize: '12px' }}
              label={{ value: 'Intensité', angle: -90, position: 'insideLeft', fill: 'var(--text)' }}
            />

            {/* Axe Y droit: Prix (échelle log) */}
            <YAxis
              yAxisId="price"
              orientation="right"
              scale="log"
              domain={['auto', 'auto']}
              tickFormatter={formatPriceLog}
              stroke="var(--muted)"
              style={{ fontSize: '12px' }}
              label={{ value: 'Prix Bitcoin (USD)', angle: 90, position: 'insideRight', fill: 'var(--text)' }}
            />

            <Tooltip content={<CustomTooltip />} />

            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
            />

            {/* Aires empilées pour les phases (conditionnelles) */}
            {layers.phases && (
              <>
                <Area
                  yAxisId="fractal"
                  type="monotone"
                  dataKey="bearMarketValue"
                  stackId="1"
                  stroke={PHASE_COLORS['Bear Market']}
                  fill="url(#bearMarketGradient)"
                  name="Bear Market"
                />
                <Area
                  yAxisId="fractal"
                  type="monotone"
                  dataKey="accumulationValue"
                  stackId="1"
                  stroke={PHASE_COLORS['Accumulation']}
                  fill="url(#accumulationGradient)"
                  name="Accumulation"
                />
                <Area
                  yAxisId="fractal"
                  type="monotone"
                  dataKey="markupValue"
                  stackId="1"
                  stroke={PHASE_COLORS['Markup']}
                  fill="url(#markupGradient)"
                  name="Markup"
                />
                <Area
                  yAxisId="fractal"
                  type="monotone"
                  dataKey="distributionValue"
                  stackId="1"
                  stroke={PHASE_COLORS['Distribution']}
                  fill="url(#distributionGradient)"
                  name="Distribution"
                />
              </>
            )}

            {/* Halving Event Markers (conditionnels) */}
            {layers.halvings && HALVING_EVENTS.map((halving, index) => (
              <ReferenceLine
                key={`halving-${index}`}
                x={halving.timestamp}
                stroke="#8b5cf6"
                strokeWidth={2}
                strokeDasharray="3 3"
                yAxisId="price"
                label={{
                  value: halving.label,
                  position: 'top',
                  fill: '#8b5cf6',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              />
            ))}

            {/* Rainbow Chart Bands (conditionnels) */}
            {layers.rainbow && (
              <>
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="rainbow1"
                  stroke="#4169E1"
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  dot={false}
                  name="🌈 Fire Sale"
                  strokeDasharray="3 3"
                />
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="rainbow3"
                  stroke="#10b981"
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  dot={false}
                  name="🌈 Accumulate"
                  strokeDasharray="3 3"
                />
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="rainbow5"
                  stroke="#fbbf24"
                  strokeWidth={1.5}
                  strokeOpacity={0.5}
                  dot={false}
                  name="🌈 Hold"
                />
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="rainbow7"
                  stroke="#f97316"
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  dot={false}
                  name="🌈 FOMO"
                  strokeDasharray="3 3"
                />
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="rainbow9"
                  stroke="#dc2626"
                  strokeWidth={1}
                  strokeOpacity={0.4}
                  dot={false}
                  name="🌈 Max Bubble"
                  strokeDasharray="3 3"
                />
              </>
            )}

            {/* Ligne de prix Bitcoin */}
            <Line
              yAxisId="price"
              type="monotone"
              dataKey="price"
              stroke="#000000"
              strokeWidth={3}
              dot={false}
              name="Prix Bitcoin"
            />

            {/* Lignes de coût de minage (fourchette min-max) - conditionnelles */}
            {layers.miningCosts && (
              <>
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="miningCostMin"
                  stroke="#10b981"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  strokeOpacity={0.6}
                  dot={false}
                  name="💰 Coût Min (Grandes fermes)"
                />
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="miningCost"
                  stroke="#f97316"
                  strokeWidth={2.5}
                  strokeDasharray="5 5"
                  dot={false}
                  name="💰 Coût Moyen (US)"
                />
                <Line
                  yAxisId="price"
                  type="monotone"
                  dataKey="miningCostMax"
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  strokeOpacity={0.6}
                  dot={false}
                  name="💰 Coût Max (Petits mineurs)"
                />
              </>
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Phase Descriptions */}
      <div className="phase-descriptions">
        <h2>{t('bitcoinCycles.phases.title')}</h2>
        <div className="phases-grid">
          <div className="phase-card" style={{ borderLeftColor: PHASE_COLORS['Bear Market'] }}>
            <h3 style={{ color: PHASE_COLORS['Bear Market'] }}>
              {t('bitcoinCycles.phases.bearMarket.title')}
            </h3>
            <p>
              {t('bitcoinCycles.phases.bearMarket.description')}
            </p>
            <div className="phase-indicators">
              {(t('bitcoinCycles.phases.bearMarket.indicators', { returnObjects: true }) as string[]).map((indicator, idx) => (
                <span key={idx}>• {indicator}</span>
              ))}
            </div>
          </div>

          <div className="phase-card" style={{ borderLeftColor: PHASE_COLORS['Accumulation'] }}>
            <h3 style={{ color: PHASE_COLORS['Accumulation'] }}>
              {t('bitcoinCycles.phases.accumulation.title')}
            </h3>
            <p>
              {t('bitcoinCycles.phases.accumulation.description')}
            </p>
            <div className="phase-indicators">
              {(t('bitcoinCycles.phases.accumulation.indicators', { returnObjects: true }) as string[]).map((indicator, idx) => (
                <span key={idx}>• {indicator}</span>
              ))}
            </div>
          </div>

          <div className="phase-card" style={{ borderLeftColor: PHASE_COLORS['Markup'] }}>
            <h3 style={{ color: PHASE_COLORS['Markup'] }}>
              {t('bitcoinCycles.phases.markup.title')}
            </h3>
            <p>
              {t('bitcoinCycles.phases.markup.description')}
            </p>
            <div className="phase-indicators">
              {(t('bitcoinCycles.phases.markup.indicators', { returnObjects: true }) as string[]).map((indicator, idx) => (
                <span key={idx}>• {indicator}</span>
              ))}
            </div>
          </div>

          <div className="phase-card" style={{ borderLeftColor: PHASE_COLORS['Distribution'] }}>
            <h3 style={{ color: PHASE_COLORS['Distribution'] }}>
              {t('bitcoinCycles.phases.distribution.title')}
            </h3>
            <p>
              {t('bitcoinCycles.phases.distribution.description')}
            </p>
            <div className="phase-indicators">
              {(t('bitcoinCycles.phases.distribution.indicators', { returnObjects: true }) as string[]).map((indicator, idx) => (
                <span key={idx}>• {indicator}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Educational Note */}
      <div className="educational-note">
        <Icons.Info size={20} />
        <div>
          <strong>{t('bitcoinCycles.educationalNote.title')}</strong> {t('bitcoinCycles.educationalNote.content')}
          <br /><br />
          <strong>{t('bitcoinCycles.educationalNote.miningCostsTitle')}</strong>
          <br />• <span style={{ color: '#10b981' }}>{t('bitcoinCycles.educationalNote.minCost')}</span>
          <br />• <span style={{ color: '#f97316' }}>{t('bitcoinCycles.educationalNote.avgCost')}</span>
          <br />• <span style={{ color: '#ef4444' }}>{t('bitcoinCycles.educationalNote.maxCost')}</span>
        </div>
      </div>
        </>
      )}

      {/* FUD Timeline Tab Content */}
      {activeTab === 'fud-timeline' && (
        <>
          {/* Stats Cards */}
          <div className="info-cards" style={{ marginBottom: '24px' }}>
            <div className="info-card">
              <div className="info-card-header">
                <Icons.AlertTriangle size={20} style={{ color: '#ef4444' }} />
                <span>{lang === 'fr' ? 'Événements FUD' : 'FUD Events'}</span>
              </div>
              <div className="info-card-value" style={{ color: '#ef4444' }}>
                {EVENT_STATS.totalFudEvents}
              </div>
              <div className="info-card-label">
                {lang === 'fr' ? 'Prédictions négatives' : 'Negative predictions'}
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-header">
                <Icons.TrendingUp size={20} style={{ color: '#10b981' }} />
                <span>{lang === 'fr' ? 'Événements Positifs' : 'Positive Events'}</span>
              </div>
              <div className="info-card-value" style={{ color: '#10b981' }}>
                {EVENT_STATS.totalPositiveEvents}
              </div>
              <div className="info-card-label">
                {lang === 'fr' ? 'Adoptions & ATH' : 'Adoptions & ATH'}
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-header">
                <Icons.AlertCircle size={20} style={{ color: '#f97316' }} />
                <span>{lang === 'fr' ? '"Bitcoin is Dead"' : '"Bitcoin is Dead"'}</span>
              </div>
              <div className="info-card-value" style={{ color: '#f97316' }}>
                {EVENT_STATS.bitcoinDeadCount}+
              </div>
              <div className="info-card-label">
                {lang === 'fr' ? 'fois déclaré mort' : 'times declared dead'}
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-header">
                <Icons.DollarSign size={20} />
                <span>{lang === 'fr' ? 'Prix Actuel' : 'Current Price'}</span>
              </div>
              <div className="info-card-value">
                ${(currentBtcPrice || data[data.length - 1]?.price || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}
              </div>
              <div className="info-card-label">
                {lang === 'fr' ? 'Toujours debout!' : 'Still standing!'}
              </div>
            </div>
          </div>

          {/* FUD Timeline Chart */}
          <div className="chart-container fud-timeline-chart" style={{ cursor: 'pointer' }}>
            <ResponsiveContainer width="100%" height={isMobile ? 400 : 600}>
              <ComposedChart
                data={fudTimelineData}
                margin={isMobile
                  ? { top: 30, right: 10, left: 0, bottom: 40 }
                  : { top: 40, right: 80, left: 20, bottom: 60 }
                }
                onClick={handleChartClick}
              >
                <defs>
                  <linearGradient id="priceGradientFud" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f7931a" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f7931a" stopOpacity={0.05} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.3} />

                <XAxis
                  dataKey="timestamp"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={formatDate}
                  stroke="var(--muted)"
                  style={{ fontSize: isMobile ? '10px' : '12px' }}
                  tick={{ fontSize: isMobile ? 10 : 12 }}
                />

                <YAxis
                  yAxisId="price"
                  orientation="right"
                  scale="log"
                  domain={['auto', 'auto']}
                  tickFormatter={formatPriceLog}
                  stroke="var(--muted)"
                  style={{ fontSize: isMobile ? '10px' : '12px' }}
                  width={isMobile ? 45 : 60}
                  label={isMobile ? undefined : { value: lang === 'fr' ? 'Prix Bitcoin (USD)' : 'Bitcoin Price (USD)', angle: 90, position: 'insideRight', fill: 'var(--text)' }}
                />

                <Tooltip content={<FudTimelineTooltip />} />

                {/* Price Area */}
                <Area
                  yAxisId="price"
                  type="monotone"
                  dataKey="price"
                  stroke="#f7931a"
                  strokeWidth={isMobile ? 1.5 : 2}
                  fill="url(#priceGradientFud)"
                  name={lang === 'fr' ? 'Prix Bitcoin' : 'Bitcoin Price'}
                />

                {/* Event Reference Lines with Labels */}
                {filteredEvents.map((event, index) => (
                  <ReferenceLine
                    key={event.id}
                    x={event.timestamp}
                    yAxisId="price"
                    stroke={getEventColor(event.type)}
                    strokeWidth={isMobile ? 1 : 2}
                    strokeDasharray={event.type === 'fud' ? '5 5' : '0'}
                    strokeOpacity={isMobile ? 0.6 : 0.8}
                    label={isMobile ? undefined : {
                      value: event.icon,
                      position: index % 2 === 0 ? 'top' : 'insideTop',
                      fill: getEventColor(event.type),
                      fontSize: 16,
                    }}
                  />
                ))}

                {/* Brush for zoom - below the chart */}
                <Brush
                  dataKey="timestamp"
                  height={isMobile ? 30 : 40}
                  stroke="var(--accent)"
                  fill="var(--surface)"
                  tickFormatter={formatDate}
                  startIndex={0}
                  endIndex={fudTimelineData.length - 1}
                />

                <Legend
                  wrapperStyle={{ paddingTop: isMobile ? '8px' : '20px' }}
                  content={() => (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: isMobile ? '12px' : '24px',
                      paddingTop: isMobile ? '8px' : '16px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: isMobile ? '14px' : '20px', height: '3px', background: '#ef4444', borderRadius: '2px' }} />
                        <span style={{ color: 'var(--muted)', fontSize: isMobile ? '10px' : '12px' }}>FUD</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: isMobile ? '14px' : '20px', height: '3px', background: '#10b981', borderRadius: '2px' }} />
                        <span style={{ color: 'var(--muted)', fontSize: isMobile ? '10px' : '12px' }}>{lang === 'fr' ? 'Positif' : 'Positive'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ width: isMobile ? '14px' : '20px', height: '3px', background: '#f7931a', borderRadius: '2px' }} />
                        <span style={{ color: 'var(--muted)', fontSize: isMobile ? '10px' : '12px' }}>{lang === 'fr' ? 'Prix BTC' : 'BTC Price'}</span>
                      </div>
                    </div>
                  )}
                />
              </ComposedChart>
            </ResponsiveContainer>
            {/* Mobile zoom hint */}
            {isMobile && (
              <div style={{
                textAlign: 'center',
                marginTop: '8px',
                fontSize: '11px',
                color: 'var(--muted)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}>
                <Icons.Maximize size={14} />
                {lang === 'fr' ? 'Glissez la barre ci-dessous pour zoomer' : 'Drag the bar below to zoom'}
              </div>
            )}
          </div>

          {/* Events Timeline List */}
          <div className="phase-descriptions" style={{ marginTop: isMobile ? '16px' : '32px' }}>
            <h3 style={{ marginBottom: isMobile ? '12px' : '24px', fontSize: isMobile ? '16px' : '20px' }}>
              {lang === 'fr' ? 'Chronologie des Événements' : 'Events Timeline'}
              {isMobile && <span style={{ fontSize: '12px', color: 'var(--muted)', marginLeft: '8px' }}>
                ({filteredEvents.length} {lang === 'fr' ? 'majeurs' : 'major'})
              </span>}
            </h3>
            <div style={{
              display: 'grid',
              gap: isMobile ? '8px' : '16px',
              maxHeight: isMobile ? '400px' : '600px',
              overflowY: 'auto',
              padding: '4px'
            }}>
              {[...filteredEvents].reverse().map((event) => (
                <div
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderLeft: `4px solid ${getEventColor(event.type)}`,
                    borderRadius: isMobile ? '8px' : '12px',
                    padding: isMobile ? '10px 12px' : '16px 20px',
                    display: 'flex',
                    gap: isMobile ? '10px' : '16px',
                    alignItems: 'flex-start',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{ fontSize: isMobile ? '24px' : '32px' }}>{event.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: isMobile ? '4px' : '8px',
                      flexWrap: 'wrap',
                      gap: isMobile ? '4px' : '8px'
                    }}>
                      <h3 style={{ margin: 0, color: 'var(--text)', fontSize: isMobile ? '13px' : '16px', lineHeight: 1.3 }}>
                        {event.title[lang]}
                      </h3>
                      <div style={{ display: 'flex', gap: isMobile ? '6px' : '12px', alignItems: 'center', flexShrink: 0 }}>
                        <span style={{
                          fontSize: isMobile ? '10px' : '12px',
                          padding: isMobile ? '2px 6px' : '4px 8px',
                          borderRadius: '4px',
                          background: `${getEventColor(event.type)}20`,
                          color: getEventColor(event.type),
                          fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>
                          {event.type === 'fud' ? 'FUD' : event.type === 'positive' ? (lang === 'fr' ? 'Positif' : 'Positive') : 'Neutral'}
                        </span>
                        <span style={{ fontSize: isMobile ? '10px' : '12px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          {new Date(event.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: isMobile ? undefined : 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    {!isMobile && (
                      <p style={{ margin: '0 0 8px 0', color: 'var(--muted)', fontSize: '14px', lineHeight: '1.5' }}>
                        {event.description[lang]}
                      </p>
                    )}
                    {event.priceAtEvent && (
                      <div style={{ fontSize: isMobile ? '11px' : '13px' }}>
                        <span style={{ color: 'var(--muted)' }}>
                          {lang === 'fr' ? 'Prix:' : 'Price:'}
                        </span>
                        <span style={{ fontWeight: 600, color: '#f7931a', marginLeft: '4px' }}>
                          ${event.priceAtEvent.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Educational Note for FUD */}
          <div className="educational-note" style={{ marginTop: '32px' }}>
            <Icons.Info size={20} />
            <div>
              <strong>{lang === 'fr' ? 'Malgré tout le FUD...' : 'Despite all the FUD...'}</strong>{' '}
              {lang === 'fr'
                ? `Bitcoin a été déclaré "mort" plus de ${EVENT_STATS.bitcoinDeadCount} fois, la Chine l'a "interdit" ${EVENT_STATS.chinaBanCount} fois, et pourtant il continue de battre des records. De $0.01 en 2010 à plus de $100,000 en 2024 - une hausse de plus de 10,000,000%. Le FUD est éternel, Bitcoin aussi.`
                : `Bitcoin has been declared "dead" over ${EVENT_STATS.bitcoinDeadCount} times, China has "banned" it ${EVENT_STATS.chinaBanCount} times, and yet it keeps breaking records. From $0.01 in 2010 to over $100,000 in 2024 - a rise of over 10,000,000%. FUD is eternal, so is Bitcoin.`
              }
            </div>
          </div>
        </>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal />
    </div>
  );
}
